import React, { useEffect, useState } from 'react';

import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, ResponsiveContainer } from 'recharts';

import './Dashboard.css';

export const QualityControlDashboard = ({ dashboard, renderDuration }) => {
  const [allOngoingCAPAs, setAllOngoingCAPAs] = useState(0);
  const [allInReviewCAPAs, setAllInReviewCAPAs] = useState(0);
  const [allOverdueCAPAs, setAllOverdueCAPAs] = useState(0);
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    if (dashboard) {
      setAllOngoingCAPAs(dashboard.ongoingCAPAS || 0);
      setAllInReviewCAPAs(dashboard.inReviewCAPAS || 0);
      setAllOverdueCAPAs(dashboard.overdueCAPAS || 0);

      const studiesData = [...(dashboard.qualityControlDashboardVOS || [])];

      studiesData.sort((a, b) => {
        if (b.overdueOpenQueries !== a.overdueOpenQueries) {
          return b.overdueOpenQueries - a.overdueOpenQueries;
        }

        if (b.overdueCAPAs !== a.overdueCAPAs) {
          return b.overdueCAPAs - a.overdueCAPAs;
        }

        if (b.openQueries !== a.openQueries) {
          return b.openQueries - a.openQueries;
        }

        if (b.ongoingCAPAs !== a.ongoingCAPAs) {
          return b.ongoingCAPAs - a.ongoingCAPAs;
        }

        if (b.inReviewCAPAs !== a.inReviewCAPAs) {
          return b.inReviewCAPAs - a.inReviewCAPAs;
        }

        return 0;
      });

      setStudies(studiesData);
    }
  }, [dashboard]);

  const parseDurationDates = (duration) => {
    if (!duration) {
      return {
        startDate: null,
        endDate: null,
      };
    }

    const [startPart, endPart] = duration.split(' - ');

    return {
      startDate: startPart?.split(' ')[0] || null,
      endDate: endPart?.split(' ')[0] || null,
    };
  };

  const mergeData = (openQueriesData = [], ongoingCapasData = [], duration) => {
    const { startDate, endDate } = parseDurationDates(duration);
    const mergedData = {};

    if (startDate) {
      mergedData[startDate] = {
        ...mergedData[startDate],
        date: startDate,
      };
    }

    if (endDate) {
      mergedData[endDate] = {
        ...mergedData[endDate],
        date: endDate,
      };
    }

    openQueriesData.forEach((item) => {
      const { date, count } = item;

      mergedData[date] = {
        ...mergedData[date],
        date,
        openQueries: count,
      };
    });

    ongoingCapasData.forEach((item) => {
      const { date, count } = item;

      mergedData[date] = {
        ...mergedData[date],
        date,
        ongoingCapas: count,
      };
    });

    return Object.values(mergedData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  return (
    <div className="dashboard">
      <div className="dashboard-section">
        <div
          className={`dashboard-container ${allOngoingCAPAs > 0 ? 'red-border' : ''} ${
            allOverdueCAPAs > 0 ? 'red-background' : ''
          }`}
        >
          <label className="dashboard-header-label">CAPA Reviews</label>

          <div className="section">
            <h5>Ongoing CAPAs: {allOngoingCAPAs}</h5>
            <h5>In Review CAPAs: {allInReviewCAPAs}</h5>
            <h5>Overdue CAPAs: {allOverdueCAPAs}</h5>
          </div>
        </div>

        {studies &&
          studies.map((study, index) => {
            const mergedData = mergeData(study.openQueriesOverDuration, study.ongoingCAPASOverDuration, study.duration);

            const { startDate, endDate } = parseDurationDates(study.duration);

            const xAxisTicks = [startDate, endDate].filter(Boolean);

            return (
              <div
                key={index}
                className={`dashboard-container
                  ${study.openQueries > 0 || study.ongoingCAPAs > 0 ? 'red-border' : 'green-border'} 
                  ${study.overdueOpenQueries > 0 || study.overdueCAPAs > 0 ? 'red-background' : 'green-border'}`}
              >
                <label className="dashboard-header-label">{study.study}</label>

                <div className="section-big">
                  <div className="section-left">
                    <label className="dashboard-label">Last Updated: {study.lastUpdated || '-'}</label>

                    <label className="dashboard-label">Duration: {renderDuration(study.duration)}</label>

                    <label className="dashboard-label">Site: {study.site || '-'}</label>

                    <label className="dashboard-label blue-label">Participants: {study.participants || '-'}</label>
                  </div>

                  <div className="section-mid">
                    <ResponsiveContainer height={150} width="95%" debounce={50}>
                      <LineChart
                        data={mergedData}
                        margin={{
                          top: 10,
                          right: 40,
                          bottom: 5,
                          left: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="4 4" />

                        <XAxis dataKey="date" type="category" ticks={xAxisTicks} interval={0} padding={{ right: 10 }} />

                        <YAxis width={35} tickMargin={5} />

                        <Tooltip />

                        <Line type="monotone" dataKey="openQueries" stroke="#FF0000" />

                        <Line type="monotone" dataKey="ongoingCapas" stroke="#077187" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="section-right">
                    <label
                      className={`dashboard-label ${
                        study.openQueries > 0 || study.ongoingCAPAs > 0 ? 'red-label' : ''
                      } red-graph-data`}
                    >
                      Open Queries: {study.openQueries}
                    </label>

                    <label
                      className={`dashboard-label ${
                        study.overdueOpenQueries > 0 || study.overdueCAPAs > 0 ? 'red-label' : ''
                      } normal`}
                    >
                      Overdue Open Queries: {study.overdueOpenQueries}
                    </label>

                    <label className="dashboard-label blue-label blue-graph-data">
                      Ongoing CAPAs: {study.ongoingCAPAs}
                    </label>

                    <label
                      className={`dashboard-label ${
                        study.overdueOpenQueries > 0 || study.overdueCAPAs > 0 ? 'red-label' : ''
                      } normal`}
                    >
                      In Review CAPAs: {study.inReviewCAPAs}
                    </label>

                    <label
                      className={`dashboard-label ${
                        study.overdueOpenQueries > 0 || study.overdueCAPAs > 0 ? 'red-label' : ''
                      } normal`}
                    >
                      Overdue CAPAs: {study.overdueCAPAs}
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
